import { useVoiceSource } from '#pplx/frontend/apps/ask/multimodal/media/source.js';
import { describeSpeechRecognitionError } from '#pplx/frontend/apps/ask/multimodal/speech-recognition/providers/soniox/state.js';
import { createTranscriptionStore } from '#pplx/frontend/apps/ask/multimodal/speech-recognition/providers/soniox/transcriptionStore.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useEffect, useMemo, useState } from 'react';
import { useStore } from 'zustand';
const DEFAULT_SILENCE_THRESHOLD = 30;
const DEFAULT_SILENCE_DURATION_MS = 5000;
const selectState = (store) => store.state;
const selectTranscription = (state) => {
    switch (state.kind) {
        case 'active':
            return state.partialText;
        case 'completed':
        case 'failed':
            return state.text;
        case 'idle':
        case 'connecting':
            return '';
    }
};
/**
 * React adapter over `createTranscriptionStore`. Holds one store per mounted
 * hook, projects the current session's state, and releases the session on
 * unmount. Contains no lifecycle logic of its own.
 */
export const useTranscription = ({ callbacks, config, reason, }) => {
    const { track } = useInstrumentation();
    const source = useVoiceSource();
    const silenceThreshold = config.silenceThreshold ?? DEFAULT_SILENCE_THRESHOLD;
    const silenceDuration = config.silenceDuration ?? DEFAULT_SILENCE_DURATION_MS;
    const [store] = useState(() => createTranscriptionStore({
        callbacks,
        reason,
        source,
        silenceThreshold,
        silenceDuration,
        track,
    }));
    // Callbacks and attribution can change across renders; the store reads them lazily at start().
    useEffect(() => {
        store
            .getState()
            .configure({ callbacks, reason, source, silenceThreshold, silenceDuration, track });
    }, [store, callbacks, reason, source, silenceThreshold, silenceDuration, track]);
    // Release the microphone if the host unmounts mid-session: no `onStop`, no event. No-op when idle.
    useEffect(() => () => store.getState().dispose(), [store]);
    const state = useStore(store, selectState);
    // Support never changes after construction, so read it once rather than subscribing.
    const { isSupported, start, stop } = store.getState();
    return useMemo(() => ({
        isSupported,
        isTranscribing: state.kind === 'active',
        isInitializing: state.kind === 'connecting',
        transcription: selectTranscription(state),
        error: state.kind === 'failed' ? describeSpeechRecognitionError(state.error) : null,
        state,
        startTranscription: start,
        stopTranscription: stop,
    }), [isSupported, state, start, stop]);
};
//# sourceMappingURL=useTranscription.js.map